import {useEffect, useRef, useState} from "react";
import {FiArrowRightCircle, FiBook, FiPlus, FiPlusCircle} from "react-icons/fi";
import {useNavigate} from "react-router-dom";
import {toast} from "react-toastify";
import {useTranslation} from "react-i18next";
import ActionsMenu from "../../components/common/actionsMenu/ActionsMenu";
import ConfirmDeleteModal from "../../components/common/deleteModal/ConfirmDeleteModal";
import PageHeader from "../../components/common/pageheader/PageHeader";
import {
    createExamBase,
    deleteExamBase, duplicateExamBase,
    getExamBases,
    getWizardDrafts,
    updateExamBase,
} from "../../services/examBasesApi";
import {getSubjectsWithQuestionCount} from "../../services/subjectsApi";
import ExamBaseFormModal from "./ExamBaseFormModal/ExamBaseFormModal";
import styles from "./ExamBasePage.module.css";
import ExamBaseStudentsModal from "./ExamBaseStudentsModal/ExamBaseStudentsModal";
import Loader from "../../components/common/Loader/Loader";
import TableEmptyState from "../../components/common/tableEmptyState/TableEmptyState";
import ExamBaseDuplicateModal from "../../components/common/examBaseDuplicateModal/ExamBaseDuplicateModal";

// Subject column badge for multi-subject templates: click to reveal the full subject list in a
// popover that closes on outside-click / Escape / scroll. Positioned with position:fixed (from the
// badge's bounding rect) so the table's `overflow:hidden` rounded-corner clip can't crop it.
const MultiSubjectBadge = ({subjects, label}) => {
    const [open, setOpen] = useState(false);
    const [coords, setCoords] = useState({top: 0, left: 0});
    const wrapRef = useRef(null);
    const btnRef = useRef(null);
    const popoverRef = useRef(null);

    const toggle = () => {
        if (open) {
            setOpen(false);
            return;
        }
        const rect = btnRef.current.getBoundingClientRect();
        setCoords({top: rect.bottom + 6, left: rect.left});
        setOpen(true);
    };

    useEffect(() => {
        if (!open) return undefined;
        const onPointerDown = (event) => {
            if (
                !wrapRef.current?.contains(event.target) &&
                !popoverRef.current?.contains(event.target)
            ) {
                setOpen(false);
            }
        };
        const onKeyDown = (event) => {
            if (event.key === "Escape") setOpen(false);
        };
        const close = () => setOpen(false);
        document.addEventListener("mousedown", onPointerDown);
        document.addEventListener("keydown", onKeyDown);
        // A fixed popover doesn't track the anchor on scroll/resize, so dismiss it instead.
        window.addEventListener("scroll", close, true);
        window.addEventListener("resize", close);
        return () => {
            document.removeEventListener("mousedown", onPointerDown);
            document.removeEventListener("keydown", onKeyDown);
            window.removeEventListener("scroll", close, true);
            window.removeEventListener("resize", close);
        };
    }, [open]);

    return (
        <span className={styles.multiSubjectWrap} ref={wrapRef}>
            <button
                ref={btnRef}
                type="button"
                className={styles.multiSubjectBadge}
                onClick={toggle}
                aria-haspopup="true"
                aria-expanded={open}
            >
                {label}
            </button>
            {open && (
                <div
                    ref={popoverRef}
                    className={styles.multiSubjectPopover}
                    style={{top: coords.top, left: coords.left}}
                    role="dialog"
                >
                    <ul className={styles.multiSubjectList}>
                        {subjects.map((s, index) => (
                            <li key={s.id ?? index}>
                                {s.parentName ? `${s.parentName} › ${s.title}` : s.title}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </span>
    );
};

const ExamBasePage = () => {
    const {t} = useTranslation();
    const [examBases, setExamBases] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [titleFilter, setTitleFilter] = useState("");
    const [subjectTitleFilter, setSubjectTitleFilter] = useState("");
    const [appliedFilters, setAppliedFilters] = useState({title: "", subjectTitle: ""});

    const [pageNo, setPageNo] = useState(1);
    const [pageSize] = useState(10);
    const [totalPages, setTotalPages] = useState(0);

    const [isFormModalOpen, setIsFormModalOpen] = useState(false);
    const [formMode, setFormMode] = useState("create");
    const [editingExamBase, setEditingExamBase] = useState(null);

    const [formValues, setFormValues] = useState({
        title: "",
        durationMinutes: "",
        passRate: "",
    });
    // Multi-topic templates: the leaf topics (possibly across subjects) this template draws from.
    const [selectedTopicIds, setSelectedTopicIds] = useState([]);
    const [saving, setSaving] = useState(false);
    const [formError, setFormError] = useState("");

    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [deletingExamBase, setDeletingExamBase] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const [topicOptions, setTopicOptions] = useState([]);
    const [topicsLoading, setTopicsLoading] = useState(false);
    const [topicsError, setTopicsError] = useState("");

    const [isStudentsModalOpen, setIsStudentsModalOpen] = useState(false);
    const [studentsLoading] = useState(false);
    const [studentsError, setStudentsError] = useState("");
    const [students, setStudents] = useState([]);
    const [studentsExamBase, setStudentsExamBase] = useState(null);

    const [showDuplicateModal, setShowDuplicateModal] = useState(false);
    const [selectedExamBase, setSelectedExamBase] = useState(null);
    const [duplicating, setDuplicating] = useState(false);

    // In-progress First-Exam Wizard drafts (for the "Resume creating ..." entry points).
    const [wizardDrafts, setWizardDrafts] = useState([]);

    const navigate = useNavigate();

    const fetchWizardDrafts = async () => {
        try {
            const data = await getWizardDrafts();
            setWizardDrafts(Array.isArray(data) ? data : []);
        } catch {
            // Non-fatal — the resume strip simply stays hidden.
        }
    };

    const fetchExamBases = async () => {
        setLoading(true);
        setError("");

        try {
            setAppliedFilters({title: titleFilter || "", subjectTitle: subjectTitleFilter || ""});

            const data = await getExamBases({
                title: titleFilter || undefined,
                pageNo,
                pageSize,
            });

            let content = data.content || [];

            if (subjectTitleFilter && subjectTitleFilter.trim().length > 0) {
                const query = subjectTitleFilter.trim().toLowerCase();
                content = content.filter((item) => item.subjectTitle?.toLowerCase().includes(query));
            }

            setExamBases(content);
            setTotalPages(data.totalPages || 0);
        } catch (e) {
            const msg =
                e?.response?.data?.message ||
                e?.response?.data?.error ||
                e.message ||
                t("pages.examBase.loadError");
            setError(msg);
        } finally {
            setLoading(false);
        }
    };

    const fetchTopicOptions = async () => {
        setTopicsLoading(true);
        setTopicsError("");

        try {
            const data = await getSubjectsWithQuestionCount();
            const list = Array.isArray(data) ? data : data?.content || [];
            // Pickable topics across all subjects, each labelled with its owning subject + the
            // number of questions available in it (so the user sees the pool the exam can draw from).
            setTopicOptions(
                list.map((s) => ({
                    id: s.id,
                    title: s.title || s.name,
                    parentId: s.parentId,
                    parentName: s.parentName,
                    questionCount: s.questionCount,
                }))
            );
        } catch (e) {
            const msg =
                e?.response?.data?.message ||
                e?.response?.data?.error ||
                e.message ||
                t("pages.examBase.subjectsLoadError");
            setTopicsError(msg);
        } finally {
            setTopicsLoading(false);
        }
    };

    useEffect(() => {
        fetchExamBases();
    }, [pageNo]);

    useEffect(() => {
        fetchTopicOptions();
        fetchWizardDrafts();
    }, []);

    const handleSearchSubmit = (e) => {
        e.preventDefault();
        setPageNo(1);
        fetchExamBases();
    };

    const resetForm = () => {
        setFormValues({
            title: "",
            durationMinutes: "",
            passRate: "",
        });
        setSelectedTopicIds([]);
        setFormError("");
        setSaving(false);
    };

    const openDuplicateModal = (examBase) => {
        setShowDuplicateModal(true);
        setSelectedExamBase(examBase);
    }

    const openCreateModal = () => {
        setFormMode("create");
        setEditingExamBase(null);
        resetForm();
        setIsFormModalOpen(true);
    };

    const openEditModal = (examBase) => {
        setFormMode("edit");
        setEditingExamBase(examBase);
        setFormValues({
            title: examBase.title || "",
            durationMinutes: examBase.durationMinutes || "",
            passRate: examBase.passRate || "",
        });
        // Preload the template's current topic set (fall back to the legacy single subject).
        setSelectedTopicIds(
            Array.isArray(examBase.subjects) && examBase.subjects.length > 0
                ? examBase.subjects.map((s) => Number(s.id))
                : examBase.subjectId ? [Number(examBase.subjectId)] : []
        );
        setFormError("");
        setSaving(false);
        setIsFormModalOpen(true);
    };

    const closeFormModal = () => {
        setIsFormModalOpen(false);
        setEditingExamBase(null);
        resetForm();
    };

    const handleConfirmDuplicateModal = async () => {
        setDuplicating(true);
        try {
            setError("");
            await duplicateExamBase(selectedExamBase.id);
            toast.success(t("pages.examBase.duplicateSuccess"));
            handleCloseDuplicateModal();
            fetchExamBases();
        } catch (e) {
            const msg =
                e?.response?.data?.message ||
                e?.response?.data?.error ||
                e.message ||
                t("pages.examBase.duplicateError");
            setError(msg);
        } finally {
            setDuplicating(false);
        }
    }

    const handleCloseDuplicateModal = () => {
        setShowDuplicateModal(false);
        setDeletingExamBase(null);
        setDuplicating(false);
    }

    const handleFormChange = (e) => {
        const {name, value} = e.target;
        setFormValues((prev) => ({...prev, [name]: value}));
    };

    const toggleTopic = (topicId) => {
        const id = Number(topicId);
        setSelectedTopicIds((prev) =>
            prev.includes(id) ? prev.filter((existing) => existing !== id) : [...prev, id]
        );
    };

    // Bulk select/clear all topics of one subject at once (the group's "Select all" / "Clear" action),
    // so the user can build a general exam spanning every topic of a subject in one click.
    const toggleTopicGroup = (topicIds, shouldSelect) => {
        const numIds = (topicIds || []).map(Number);
        setSelectedTopicIds((prev) => {
            const set = new Set(prev.map(Number));
            if (shouldSelect) numIds.forEach((id) => set.add(id));
            else numIds.forEach((id) => set.delete(id));
            return Array.from(set);
        });
    };

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setFormError("");

        try {
            if (formMode === "edit" && editingExamBase) {
                await updateExamBase({
                    id: editingExamBase.id,
                    title: formValues.title,
                    durationMinutes: Number(formValues.durationMinutes),
                    passRate: Number(formValues.passRate),
                    subjectIds: selectedTopicIds,
                });
                toast.success(t("pages.examBase.updateSuccess"));
            } else {
                await createExamBase({
                    title: formValues.title,
                    durationMinutes: Number(formValues.durationMinutes),
                    passRate: Number(formValues.passRate),
                    subjectIds: selectedTopicIds,
                });
                toast.success(t("pages.examBase.createSuccess"));
            }
            closeFormModal();
            fetchExamBases();
        } catch (e) {
            const msg =
                e?.response?.data?.message ||
                e?.response?.data?.error ||
                e.message ||
                t("pages.examBase.operationFailed");
            setFormError(msg);
        } finally {
            setSaving(false);
        }
    };

    const openDeleteModal = (examBase) => {
        setError("");
        setDeletingExamBase(examBase);
        setIsDeleteModalOpen(true);
    };

    const closeDeleteModal = () => {
        setDeletingExamBase(null);
        setIsDeleteModalOpen(false);
    };

    const handleConfirmDelete = async () => {
        if (!deletingExamBase) return;
        try {
            setDeleting(true);
            setError("");
            await deleteExamBase(deletingExamBase.id);
            toast.success(t("pages.examBase.deleteSuccess"));
            closeDeleteModal();
            fetchExamBases();
        } catch (e) {
            const msg =
                e?.response?.data?.message ||
                e?.response?.data?.error ||
                e.message ||
                t("pages.examBase.deleteFailed");
            setError(msg);
        } finally {
            setDeleting(false);
        }
    };

    const goToPrevPage = () => {
        setPageNo((prev) => Math.max(prev - 1, 1));
    };

    const goToNextPage = () => {
        if (pageNo < totalPages) {
            setPageNo((prev) => prev + 1);
        }
    };

    const closeStudentsModal = () => {
        setIsStudentsModalOpen(false);
        setStudents([]);
        setStudentsError("");
        setStudentsExamBase(null);
    };


    return (
        <div className={styles.container}>
            <PageHeader
                title={t("pages.examBase.title")}
                icon={<FiBook/>}
                description={t("pages.examBase.subtitle")}
            />

            <form className={styles.filterForm} onSubmit={handleSearchSubmit}>
                <input
                    type="text"
                    placeholder={t("pages.examBase.searchByTitle")}
                    className={styles.filterInput}
                    value={titleFilter}
                    onChange={(e) => setTitleFilter(e.target.value)}
                />

                <input
                    type="text"
                    placeholder={t("pages.examBase.searchBySubject")}
                    className={styles.filterInput}
                    value={subjectTitleFilter}
                    onChange={(e) => setSubjectTitleFilter(e.target.value)}
                />

                <button type="submit" className={styles.primaryButton}>
                    {t("common.search")}
                </button>

                <button type="button" className={styles.primaryButton} onClick={() => navigate("/exams/wizard")}>
                    <FiPlusCircle/>
                    {t("pages.examBase.createExam")}
                </button>

                <button type="button" className={styles.secondaryButton} onClick={openCreateModal}>
                    <FiPlus/>
                    {t("pages.examBase.newTemplate")}
                </button>
            </form>

            {wizardDrafts.length > 0 && (
                <div className={styles.resumeBanner}>
                    <div className={styles.resumeHead}>
                        <span className={styles.resumeTitle}>{t("pages.examBase.resumeTitle")}</span>
                    </div>
                    <div className={styles.resumeList}>
                        {wizardDrafts.map((draft) => (
                            <button
                                key={draft.id}
                                type="button"
                                className={styles.resumeCard}
                                onClick={() => navigate(`/exams/wizard/${draft.id}`)}
                            >
                                <span className={styles.resumeInfo}>
                                    <span className={styles.resumeName}>{draft.title}</span>
                                    <span className={styles.resumeStep}>
                                        {t("pages.examBase.resumeStep", {step: draft.wizardStep || 1})}
                                    </span>
                                </span>
                                <FiArrowRightCircle size={18}/>
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {error && <div className={styles.error}>{error}</div>}
            {topicsError && <div className={styles.error}>{topicsError}</div>}

            <div className={styles.tableWrapper}>
                {loading ? (
                    <Loader fullscreen/>
                ) : examBases.length === 0 ? (
                    <TableEmptyState
                        title={t("pages.examBase.noTemplatesFound")}
                        description={appliedFilters.title || appliedFilters.subjectTitle ? t("pages.examBase.changeCriteria") : t("pages.examBase.addNew")}
                        icon={appliedFilters.title || appliedFilters.subjectTitle ? "search" : "inbox"}
                        action={
                            !(appliedFilters.title || appliedFilters.subjectTitle) ? (
                                <button
                                    type="button"
                                    className={styles.primaryButton}
                                    onClick={() => navigate("/exams/wizard")}
                                >
                                    <FiPlusCircle/>
                                    {t("pages.examBase.createFirstExam")}
                                </button>
                            ) : undefined
                        }
                    />
                ) : (
                    <table className={styles.table}>
                        <thead>
                        <tr>
                            <th>{t("pages.examBase.tableTitle")}</th>
                            <th>{t("pages.examBase.tableSubject")}</th>
                            <th>{t("pages.examBase.tableDuration")}</th>
                            <th>{t("pages.examBase.tablePassRate")}</th>
                            <th></th>
                        </tr>
                        </thead>
                        <tbody>
                        {examBases.map((examBase) => (
                            <tr key={examBase.id}>
                                <td>{examBase.title}</td>
                                <td>
                                    {examBase.multiSubject ? (
                                        <MultiSubjectBadge
                                            subjects={examBase.subjects || []}
                                            label={t("pages.examBase.multipleSubjects")}
                                        />
                                    ) : (
                                        examBase.subjectTitle ||
                                        (examBase.subjects && examBase.subjects[0] && examBase.subjects[0].title) ||
                                        "-"
                                    )}
                                </td>
                                <td>{examBase.durationMinutes}</td>
                                <td>{examBase.passRate}</td>
                                <td className={styles.actionsCell}>
                                    <div className={styles.actions}>
                                        <button
                                            type="button"
                                            className={styles.iconButton}
                                            title={t("pages.examBase.viewQuestions")}
                                            onClick={() => navigate(`/exam-bases/${examBase.id}/questions`)}
                                        >
                                            <FiArrowRightCircle size={18}/>
                                        </button>


                                        <ActionsMenu duplicate={() => openDuplicateModal(examBase)}
                                                     onEdit={() => openEditModal(examBase)}
                                                     onDelete={() => openDeleteModal(examBase)}/>
                                    </div>
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                )}
            </div>

            <div className={styles.pagination}>
                <button type="button" onClick={goToPrevPage} disabled={pageNo === 1}>
                    {t("common.previous")}
                </button>
                <span>
            {t("common.pageOf", {page: pageNo, total: totalPages || 1})}
          </span>
                <button type="button" onClick={goToNextPage} disabled={pageNo >= totalPages}>
                    {t("common.next2")}
                </button>
            </div>

            <ExamBaseDuplicateModal
                isOpen={showDuplicateModal}
                onConfirm={handleConfirmDuplicateModal}
                onCancel={handleCloseDuplicateModal}
                disabled={duplicating}
            />

            <ExamBaseFormModal
                isOpen={isFormModalOpen}
                mode={formMode}
                values={formValues}
                onChange={handleFormChange}
                onClose={closeFormModal}
                onSubmit={handleFormSubmit}
                saving={saving}
                error={formError}
                topicOptions={topicOptions}
                topicsLoading={topicsLoading}
                selectedTopicIds={selectedTopicIds}
                onToggleTopic={toggleTopic}
                onToggleGroup={toggleTopicGroup}
            />

            <ConfirmDeleteModal
                isOpen={isDeleteModalOpen}
                onConfirm={handleConfirmDelete}
                onCancel={closeDeleteModal}
                message={
                    deletingExamBase
                        ? t("pages.examBase.deleteConfirm", {title: deletingExamBase.title})
                        : t("pages.examBase.deleteConfirmFallback")
                }
                disabled={deleting}
            />

            <ExamBaseStudentsModal
                isOpen={isStudentsModalOpen}
                onClose={closeStudentsModal}
                loading={studentsLoading}
                error={studentsError}
                students={students}
                examBase={studentsExamBase}
            />
        </div>
    );
};

export default ExamBasePage;
